import React, { useEffect, useMemo, useState } from 'react';
import {
  CURRICULUM_PLANNER_PRIVACY_BODY,
  CURRICULUM_PLANNER_PRIVACY_TITLE,
  CURRICULUM_PLANNER_TERMS_BODY,
  CURRICULUM_PLANNER_TERMS_TITLE,
} from '../../constants/signupLegalKorean';
import { fetchSignupLegalDocuments } from '../../services/signupLegalService';
import SignUpLegalDetailModal from './SignUpLegalDetailModal';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

type LegalDetailKind = 'terms' | 'privacy';

export interface LegalConsentVersions {
  termsVersion: string;
  privacyVersion: string;
}

interface LegalConsentModalProps {
  open: boolean;
  /** 로그인 직후 동의 기록이 없는 계정에 보여주는 1회성 게이트 안내 문구 */
  heading: string;
  description: string;
  confirmLabel: string;
  busy?: boolean;
  onAgree: (versions: LegalConsentVersions) => void | Promise<void>;
  /** 없으면 취소 버튼을 숨긴다(강제 동의 게이트) */
  onCancel?: () => void;
  cancelLabel?: string;
}

/**
 * 로그인 직후(Google 자동 회원가입 포함) 동의 기록이 전혀 없는 계정에 1회성으로 보여주는
 * 약관·개인정보 동의 게이트 모달. SignUpModal의 체크박스 동의 UI와 동일한 패턴.
 */
const LegalConsentModal: React.FC<LegalConsentModalProps> = ({
  open,
  heading,
  description,
  confirmLabel,
  busy = false,
  onAgree,
  onCancel,
  cancelLabel = '취소',
}) => {
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [legalDetail, setLegalDetail] = useState<LegalDetailKind | null>(null);
  const [serverLegal, setServerLegal] = useState({
    terms_html: '',
    privacy_html: '',
    terms_updated_at: '',
    privacy_updated_at: '',
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fetchSignupLegalDocuments().then((leg) => {
      if (!cancelled) setServerLegal(leg);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (open) return;
    setAgreeTerms(false);
    setAgreePrivacy(false);
    setLegalDetail(null);
  }, [open]);

  const termsDisplayHtml = useMemo(() => {
    const t = serverLegal.terms_html.trim();
    if (t) return t;
    return `<pre class="cp-auth-legal-pre">${escapeHtml(CURRICULUM_PLANNER_TERMS_BODY)}</pre>`;
  }, [serverLegal.terms_html]);

  const privacyDisplayHtml = useMemo(() => {
    const t = serverLegal.privacy_html.trim();
    if (t) return t;
    return `<pre class="cp-auth-legal-pre">${escapeHtml(CURRICULUM_PLANNER_PRIVACY_BODY)}</pre>`;
  }, [serverLegal.privacy_html]);

  if (!open) return null;

  const canConfirm = agreeTerms && agreePrivacy && !busy;

  const handleConfirm = () => {
    if (!canConfirm) return;
    void onAgree({
      termsVersion: serverLegal.terms_updated_at,
      privacyVersion: serverLegal.privacy_updated_at,
    });
  };

  return (
    <>
      <div
        className="cp-modal-backdrop cp-auth-modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-legal-consent-gate-title"
      >
        <div className="cp-modal cp-auth-modal cp-auth-modal--wide" onClick={(e) => e.stopPropagation()}>
          <h2 id="cp-legal-consent-gate-title" className="cp-auth-modal-title">
            {heading}
          </h2>
          <p className="cp-auth-modal-hint">{description}</p>

          <section className="cp-auth-legal-block cp-auth-legal-block--compact">
            <h3 className="cp-auth-legal-heading">약관 동의 (필수)</h3>
            <button
              type="button"
              className={'cp-auth-legal-consent-row' + (agreeTerms ? ' is-agreed' : '')}
              onClick={() => setLegalDetail('terms')}
            >
              <input
                type="checkbox"
                readOnly
                tabIndex={-1}
                checked={agreeTerms}
                className="cp-auth-legal-checkbox-accent"
              />
              <span className="cp-auth-legal-consent-text">
                <span className="cp-auth-legal-consent-title">{CURRICULUM_PLANNER_TERMS_TITLE}</span>
                <span className="cp-auth-legal-consent-hint">탭하여 전문 보기 및 동의</span>
              </span>
            </button>
            <button
              type="button"
              className={'cp-auth-legal-consent-row' + (agreePrivacy ? ' is-agreed' : '')}
              onClick={() => setLegalDetail('privacy')}
            >
              <input
                type="checkbox"
                readOnly
                tabIndex={-1}
                checked={agreePrivacy}
                className="cp-auth-legal-checkbox-accent"
              />
              <span className="cp-auth-legal-consent-text">
                <span className="cp-auth-legal-consent-title">{CURRICULUM_PLANNER_PRIVACY_TITLE}</span>
                <span className="cp-auth-legal-consent-hint">탭하여 전문 보기 및 동의</span>
              </span>
            </button>
          </section>

          <div className="cp-modal-footer cp-leave-modal-actions">
            {onCancel ? (
              <button type="button" className="cp-btn-secondary" onClick={onCancel} disabled={busy}>
                {cancelLabel}
              </button>
            ) : null}
            <button type="button" className="cp-btn-primary" disabled={!canConfirm} onClick={handleConfirm}>
              {busy ? '처리 중…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>

      {legalDetail === 'terms' ? (
        <SignUpLegalDetailModal
          title={CURRICULUM_PLANNER_TERMS_TITLE}
          bodyHtml={termsDisplayHtml}
          emptyFallback="표시할 약관 본문이 없습니다."
          onCancel={() => setLegalDetail(null)}
          onConfirm={() => {
            setAgreeTerms(true);
            setLegalDetail(null);
          }}
        />
      ) : null}
      {legalDetail === 'privacy' ? (
        <SignUpLegalDetailModal
          title={CURRICULUM_PLANNER_PRIVACY_TITLE}
          bodyHtml={privacyDisplayHtml}
          emptyFallback="표시할 개인정보처리방침 본문이 없습니다."
          onCancel={() => setLegalDetail(null)}
          onConfirm={() => {
            setAgreePrivacy(true);
            setLegalDetail(null);
          }}
        />
      ) : null}
    </>
  );
};

export default LegalConsentModal;
